/**
 * User Booking Service
 *
 * Handles submitting user booking requests (name, phone, visiting date, note)
 * to the Google Sheet via Apps Script, and loading them in the admin panel.
 */

const GOOGLE_SHEET_ENDPOINT = process.env.REACT_APP_GOOGLE_SHEET_ENDPOINT || '';

/**
 * Submit a user booking (visit request) to the UserBooking sheet
 * @param {Object} bookingData - { name, phone, visitingDate, note, propertyUrl, propertyName }
 * @returns {Promise<Object>}
 */
export async function submitUserBooking(bookingData) {
  if (!GOOGLE_SHEET_ENDPOINT) {
    console.warn('[UserBooking] No endpoint configured.');
    throw new Error('Hệ thống chưa được cấu hình. Vui lòng thử lại sau.');
  }

  const epochTimestamp = Date.now();

  try {
    await fetch(GOOGLE_SHEET_ENDPOINT, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sheet: 'UserBooking',
        epochTimestamp,
        name: bookingData.name || '',
        phone: String(bookingData.phone || ''),
        visitingDate: bookingData.visitingDate || '',
        note: bookingData.note || '',
        propertyUrl: bookingData.propertyUrl || '',
        propertyName: bookingData.propertyName || '',
        propertyId: bookingData.propertyId || '',
        district: bookingData.district || '',
      }),
    });

    return { success: true };
  } catch (error) {
    console.error('[UserBooking] Submission error:', error);
    throw new Error('Không thể gửi thông tin. Vui lòng kiểm tra kết nối và thử lại.');
  }
}

/**
 * Load all user bookings from the UserBooking sheet via the backend proxy
 * @returns {Promise<Array>}
 */
export async function loadUserBookings(forceRefresh = false) {
  try {
    const cacheBuster = forceRefresh ? `&_=${Date.now()}` : '';
    const response = await fetch(`/api/google-sheet?type=user-bookings${cacheBuster}`);
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.bookings) ? data.bookings : [];
    }
  } catch (error) {
    console.error('[UserBooking] Error loading bookings:', error);
  }
  return [];
}

/**
 * Get filtered/sorted user bookings
 * @param {Object} options - { phone, sortOrder }
 * @returns {Promise<Array>}
 */
export async function getUserBookings({ phone = '', sortOrder = 'desc', forceRefresh = false } = {}) {
  const bookings = await loadUserBookings(forceRefresh);

  let filtered = bookings.map((b) => ({
    id: b.id || b[0],
    timestamp: String(b.timestamp || b[0] || ''),
    name: String(b.name || b[1] || 'N/A'),
    phone: String(b.phone || b[2] || 'N/A'),
    visitingDate: String(b.visitingDate || b[3] || 'N/A'),
    note: String(b.note || b[4] || ''),
    propertyUrl: String(b.propertyUrl || b[5] || ''),
    propertyName: String(b.propertyName || b[6] || ''),
    propertyId: String(b.propertyId || b[7] || ''),
    district: String(b.district || b[8] || ''),
    epochTimestamp: String(b.epochTimestamp || b[9] || ''),
  }));

  // Filter by phone number
  if (phone && phone.trim()) {
    const q = phone.trim().toLowerCase();
    filtered = filtered.filter((b) => b.phone.toLowerCase().includes(q));
  }

  // Sort by timestamp (created date)
  filtered.sort((a, b) => {
    const dateA = new Date(a.timestamp || 0).getTime();
    const dateB = new Date(b.timestamp || 0).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  return filtered;
}

/**
 * Delete a user booking by its epoch timestamp (used as unique row identifier)
 * @param {string} timestamp - The epoch timestamp (milliseconds) to identify the row
 * @returns {Promise<Object>}
 */
export async function deleteUserBooking(timestamp) {
  try {
    const response = await fetch('/api/google-sheet', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'user-booking',
        timestamp: String(timestamp),
      }),
    });
    if (response.ok) {
      const data = await response.json();
      return data;
    }
    return { success: false, error: 'Delete request failed' };
  } catch (error) {
    console.error('[UserBooking] Delete error:', error);
    throw new Error('Không thể xoá đăng ký. Vui lòng thử lại.');
  }
}

const UserBookingService = {
  submitUserBooking,
  loadUserBookings,
  getUserBookings,
  deleteUserBooking,
};

export default UserBookingService;